import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Button, Divider, Stack, Tooltip } from '@mui/material';

interface Props {
  canMutate: boolean;
  isLocked: boolean;
  isDirty: boolean;
  isDeletingCatalog: boolean;
  isSubmitting: boolean;
  hasCatalog: boolean;
  onCreate: () => void;
  onCreateFromCsv: (file: File) => void;
  onDeleteCatalog: () => void;
  onSave: () => void;
  onTranslate: () => void;
  onUpload: () => void;
  onLock: () => void;
  onUnlock: () => void;
}

export default function TranslationCatalogToolbar({
  canMutate,
  isLocked,
  isDirty,
  isDeletingCatalog,
  isSubmitting,
  hasCatalog,
  onCreate,
  onCreateFromCsv,
  onDeleteCatalog,
  onSave,
  onTranslate,
  onUpload,
  onLock,
  onUnlock,
}: Props) {
  const disabledReason = canMutate ? '' : 'Requires hero-planner-admin';
  const saveDisabled = !canMutate || !hasCatalog || isLocked || !isDirty || isSubmitting;
  const createDisabled = !canMutate || isDirty || isSubmitting;
  const createFromCsvDisabled = !canMutate || isDirty || isSubmitting;
  const translateDisabled = !canMutate || !hasCatalog || isLocked || isDirty || isSubmitting;
  const uploadDisabled = !canMutate || !hasCatalog || isDirty || isSubmitting;
  const deleteCatalogDisabled = !canMutate || !hasCatalog || isLocked || isDirty || isSubmitting;
  const lockActionDisabled = !canMutate || !hasCatalog || isDirty || isSubmitting;
  const LockActionIcon = isLocked ? LockOpenIcon : LockOutlinedIcon;
  const lockActionLabel = isLocked ? 'Unlock' : 'Lock';
  const lockActionColor = isLocked ? 'warning' : 'primary';
  const handleLockAction = isLocked ? onUnlock : onLock;

  return (
    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
      <Tooltip
        title={
          createDisabled && isDirty
            ? 'Save or discard changes before creating a catalog'
            : createDisabled && disabledReason
              ? disabledReason
              : ''
        }
      >
        <span>
          <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={onCreate} disabled={createDisabled}>
            New
          </Button>
        </span>
      </Tooltip>
      <Tooltip
        title={
          createFromCsvDisabled && isDirty
            ? 'Save or discard changes before creating a catalog from CSV'
            : createFromCsvDisabled && disabledReason
              ? disabledReason
              : ''
        }
      >
        <span>
          <Button
            component="label"
            startIcon={<FileUploadOutlinedIcon />}
            variant="outlined"
            size="small"
            disabled={createFromCsvDisabled}
          >
            Create from CSV
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) {
                  onCreateFromCsv(file);
                }
              }}
            />
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={saveDisabled && disabledReason ? disabledReason : ''}>
        <span>
          <Button
            startIcon={<SaveOutlinedIcon />}
            variant="contained"
            size="small"
            onClick={onSave}
            disabled={saveDisabled}
          >
            Save
          </Button>
        </span>
      </Tooltip>
      <Tooltip
        title={
          translateDisabled && isDirty
            ? 'Save or discard changes before translating this catalog'
            : translateDisabled && disabledReason
              ? disabledReason
              : ''
        }
      >
        <span>
          <Button
            startIcon={<TranslateOutlinedIcon />}
            variant="outlined"
            size="small"
            onClick={onTranslate}
            disabled={translateDisabled}
          >
            Translate
          </Button>
        </span>
      </Tooltip>
      <Tooltip
        title={
          uploadDisabled && isDirty
            ? 'Save changes before uploading to Braze'
            : uploadDisabled && disabledReason
              ? disabledReason
              : ''
        }
      >
        <span>
          <Button
            startIcon={<UploadFileOutlinedIcon />}
            variant="outlined"
            size="small"
            onClick={onUpload}
            disabled={uploadDisabled}
          >
            Upload to Braze
          </Button>
        </span>
      </Tooltip>
      <Divider orientation="vertical" flexItem />
      <Tooltip
        title={
          lockActionDisabled && isDirty
            ? 'Save or discard changes before changing lock state'
            : lockActionDisabled && disabledReason
              ? disabledReason
              : ''
        }
      >
        <span>
          <Button
            startIcon={<LockActionIcon />}
            variant="outlined"
            size="small"
            color={lockActionColor}
            onClick={handleLockAction}
            disabled={lockActionDisabled}
          >
            {lockActionLabel}
          </Button>
        </span>
      </Tooltip>
      <Tooltip
        title={
          deleteCatalogDisabled && isDirty
            ? 'Save or discard changes before deleting this catalog'
            : deleteCatalogDisabled && isLocked
              ? 'Unlock this catalog before deleting it'
              : deleteCatalogDisabled && disabledReason
                ? disabledReason
                : ''
        }
      >
        <span>
          <Button
            startIcon={<DeleteOutlineIcon />}
            variant="outlined"
            size="small"
            color="error"
            onClick={onDeleteCatalog}
            disabled={deleteCatalogDisabled}
          >
            {isDeletingCatalog ? 'Deleting...' : 'Delete'}
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
}
